import React from "react";
import { motion } from "framer-motion";
import {
  Broom,
  Wrench,
  Tree,
  MonitorPlay,
  Confetti,
  PawPrint,
  Hammer,
  Lightning,
  Drop,
  Car,
  Cube,
  HouseLine,
} from "@phosphor-icons/react";

// -------- Cleaning & Organization --------
export function CleaningServices() {
  const groups = [
    {
      icon: HouseLine,
      title: "Home & Office Cleaning",
      items: [
        "One-time cleanings",
        "Weekly, bi-weekly, and monthly plans",
        "Deep cleanings",
        "Move-in / move-out cleanings",
        "Event and party setup & cleanup",
      ],
    },
    {
      icon: Broom,
      title: "Interior & Exterior",
      items: [
        "Interior room cleaning & organization",
        "Kitchen, bathroom, and common areas",
        "Window washing (where safe)",
        "Parking lot & outdoor cleanup",
        "Decluttering & space organization",
      ],
    },
    {
      icon: PawPrint,
      title: "Pet & House Care",
      items: [
        "Pet feeding & check-ins while you travel",
        "Basic animal care",
        "Plant watering & home watch",
        "Mail & package pickup",
        "Light tidying between visits",
      ],
    },
  ];

  return (
    <section id="services" className="relative py-24 md:py-32 bg-stone-50" data-testid="cleaning-services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-sky-700 bg-sky-50 px-3 py-1.5 rounded-full">
            <Broom size={14} weight="duotone" /> Cleaning &amp; Organization
          </div>
          <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-slate-900 tracking-tight">
            A spotless home or office, on your schedule.
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Every cleaning plan is tailored to your space, your routine, and your budget. One-time
            reset or ongoing care — we make it easy.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {groups.map((g, i) => (
            <motion.div
              key={g.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-white rounded-3xl p-8 border border-slate-100 hover:shadow-[0_18px_40px_-20px_rgba(14,165,233,0.35)] hover:-translate-y-1 transition-all duration-300"
              data-testid={`cleaning-card-${i}`}
            >
              <div className="w-12 h-12 rounded-2xl bg-sky-50 text-sky-600 flex items-center justify-center">
                <g.icon size={24} weight="duotone" />
              </div>
              <h3 className="mt-5 text-xl font-display font-semibold text-slate-900">{g.title}</h3>
              <ul className="mt-4 space-y-2.5 text-slate-600 text-[15px]">
                {g.items.map((it) => (
                  <li key={it} className="flex items-start gap-2">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-sky-400 shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------- Handyman & Repair (bento) --------
export function HandymanServices() {
  const bento = [
    {
      icon: Hammer,
      title: "Carpentry & Woodworking",
      span: "md:col-span-6 lg:col-span-7",
      items: [
        "Woodworking projects",
        "Framing & drywall work",
        "Door installation & repair",
        "Hanging pictures, mirrors, blinds",
        "Furniture & outdoor equipment assembly",
        "Floor installation",
      ],
    },
    {
      icon: Lightning,
      title: "Electrical & Lighting",
      span: "md:col-span-6 lg:col-span-5",
      items: [
        "Ceiling fan installations",
        "Interior & exterior lighting",
        "Simple fixture swaps",
        "Outlet and switch replacements",
      ],
    },
    {
      icon: Drop,
      title: "Plumbing",
      span: "md:col-span-4",
      items: ["Faucet install & repair", "Vanity install & repair", "Toilet install & repair"],
    },
    {
      icon: MonitorPlay,
      title: "Tech, A/V & Cable",
      span: "md:col-span-4",
      items: [
        "TV mounting",
        "A/V and PC setup",
        "Cable cleanup & organization",
        "Security camera installs",
      ],
    },
    {
      icon: Tree,
      title: "Yard & Exterior",
      span: "md:col-span-4",
      items: [
        "Yard & outdoor cleanup",
        "Bush trimming & basic gardening",
        "Mowing & weed eating",
        "Parking lot cleanup",
      ],
    },
    {
      icon: Car,
      title: "Vehicles & Extras",
      span: "md:col-span-7",
      items: [
        "Car cleaning (interior / exterior)",
        "Tire changing",
        "And more small projects — just ask!",
      ],
    },
    {
      icon: Cube,
      title: "Odd Jobs Welcome",
      span: "md:col-span-5",
      emphasis: true,
      items: [
        "Got a weird little project? We love those.",
        "One call, one trusted crew, done right.",
      ],
    },
  ];

  return (
    <section id="handyman" className="py-24 md:py-32 bg-white" data-testid="handyman-services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-sky-700 bg-sky-50 px-3 py-1.5 rounded-full">
            <Wrench size={14} weight="duotone" /> Handyman, Repairs &amp; Projects
          </div>
          <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-slate-900 tracking-tight">
            Residential, commercial, and new construction help — without the runaround.
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Common sense fixes and thoughtful solutions. We handle the small projects most people
            hate dealing with — and the bigger ones, too.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-5 auto-rows-auto">
          {bento.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className={`${b.span || "md:col-span-6"} rounded-3xl p-8 border transition-all duration-300 hover:-translate-y-1 ${
                b.emphasis
                  ? "bg-sky-500 text-white border-sky-500 shadow-lg shadow-sky-500/20"
                  : "bg-white border-slate-100 hover:shadow-[0_18px_40px_-20px_rgba(15,23,42,0.25)]"
              }`}
              data-testid={`handyman-card-${i}`}
            >
              <div
                className={`w-11 h-11 rounded-2xl flex items-center justify-center ${
                  b.emphasis ? "bg-white/15 text-white" : "bg-sky-50 text-sky-600"
                }`}
              >
                <b.icon size={22} weight="duotone" />
              </div>
              <h3
                className={`mt-5 text-lg md:text-xl font-display font-semibold ${
                  b.emphasis ? "text-white" : "text-slate-900"
                }`}
              >
                {b.title}
              </h3>
              <ul
                className={`mt-4 space-y-2 text-[15px] ${
                  b.emphasis ? "text-sky-50" : "text-slate-600"
                }`}
              >
                {b.items.map((it) => (
                  <li key={it} className="flex items-start gap-2">
                    <span
                      className={`mt-2 w-1.5 h-1.5 rounded-full shrink-0 ${
                        b.emphasis ? "bg-white/80" : "bg-sky-400"
                      }`}
                    />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <p className="mt-10 text-sm text-slate-500">
          Don&apos;t see your project? Ask us — we take on custom jobs of all sizes.
        </p>
      </div>
    </section>
  );
}

export function EventCleanupBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-sky-700 bg-sky-50 px-3 py-1 rounded-full">
      <Confetti size={14} weight="duotone" /> Event setup &amp; cleanup available
    </span>
  );
}
